import {NavLink, Outlet, useNavigate, useParams} from 'react-router-dom';
import styles from '../collectionsUI.module.scss';
import backIcon from 'images/back.png';
import {
  GET_COLLECTION_BY_ID,
  GET_COLLECTION_STATS,
} from 'graphql/queries/collections';
import {toast} from 'react-toastify';
import {useQuery} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {NavTabsEnum} from 'utils/constants';
import {useSelector} from 'react-redux';
import {makeSelectTabOperations} from 'redux/store/auth';

const SingleCollection = () => {
  const navigate = useNavigate();
  const {id} = useParams();

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Collections),
  );

  const {data} = useQuery(GET_COLLECTION_STATS, {
    variables: {collectionId: id},
    errorPolicy: 'all',
    onError: error => toast.error(error?.message),
  });

  // The stats endpoint has the numbers but not the identity, so the header
  // reads name, ticker and exchange from the collection itself.
  const {data: detail} = useQuery(GET_COLLECTION_BY_ID, {
    variables: {collectionId: id},
    errorPolicy: 'all',
  });

  const stats = data?.adminGetCollectionStats?.data;
  const collection = detail?.getCollection?.data;

  return (
    <div className={styles.page}>
      <button
        type="button"
        className={styles.back}
        onClick={() => navigate('/dashboard/collections')}>
        <img src={backIcon} alt="" />
        All collections
      </button>

      <div className={styles.pageHead}>
        <div className={styles.detailHead}>
          {collection?.iconUrl && (
            <img
              src={collection.iconUrl}
              alt=""
              className={styles.detailIcon}
            />
          )}
          <div className={styles.detailTitle}>
            <h1>{collection?.name ?? stats?.name ?? 'Collection'}</h1>
            <div className={styles.detailMeta}>
              {collection?.ticker && (
                <span className={styles.cellTicker}>{collection.ticker}</span>
              )}
              {collection?.exchange?.code && (
                <span className={`${styles.chip} ${styles.chipBrand}`}>
                  {collection.exchange.code}
                </span>
              )}
              <span
                className={`${styles.chip} ${styles.chipDot} ${
                  collection?.status === 'Active'
                    ? styles.chipOk
                    : styles.chipMute
                }`}>
                {collection?.status === 'Active' ? 'Active' : 'Inactive'}
              </span>
              {collection?.industry?.name && (
                <span className={`${styles.chip} ${styles.chipMute}`}>
                  {collection.industry.name}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className={styles.pageHeadActions}>
          <Button
            label="Edit collection"
            disabled={!tabOperations?.update}
            onClick={() => navigate(`/dashboard/collections/update/${id}`)}
          />
        </div>
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Constituents</span>
          <span className={styles.metricValue}>
            {collection?.members?.length ?? stats?.totalCompanies ?? 0}
          </span>
          <span className={styles.metricHint}>
            {collection?.allowUserRemoveInstrument
              ? `users may remove down to ${
                  collection?.minInstrumentCount ?? 1
                }`
              : 'users cannot remove any'}
          </span>
        </div>
        <div className={styles.metric}>
          {/* Was "Number of user". */}
          <span className={styles.metricLabel}>Investors</span>
          <span className={styles.metricValue}>{stats?.totalUsers ?? 0}</span>
          <span className={styles.metricHint}>holding this collection</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Orders</span>
          <span className={styles.metricValue}>{stats?.totalOrders ?? 0}</span>
          <span className={styles.metricHint}>all time</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Amount invested</span>
          <span className={styles.metricValue}>
            {(stats?.totalInvested ?? 0).toLocaleString()}
          </span>
          <span className={styles.metricHint}>
            {collection?.currency ?? ''}
          </span>
        </div>
      </div>

      <nav className={styles.tabs}>
        <NavLink
          to={`/dashboard/collections/${id}/constituents`}
          className={({isActive}) => (isActive ? styles.tabActive : '')}>
          Constituents
        </NavLink>
        <NavLink
          to={`/dashboard/collections/${id}/users`}
          className={({isActive}) => (isActive ? styles.tabActive : '')}>
          Invested users
        </NavLink>
      </nav>

      <Outlet />
    </div>
  );
};

export {SingleCollection};
